import type {
  DocumentTab,
  LotoPanelState,
  RegionHighlight,
} from 'features/knowledgeDocumentViewer/types'
import { WORKSPACE_SETTINGS_DESTINATION } from 'features/navbar/configSettings/workspaceSettingsLink'
import { SelectedTab } from 'features/navbar/types'

/**
 * Destinations that deduplicate by stable identity instead of the legacy
 * destinationKey + pathname match. Sidebar clicks focus the existing tab and
 * preserve its state; explicit deep links focus it and navigate. Existing
 * destinations keep their current matching behavior.
 */
export const STABLE_IDENTITY_DESTINATIONS: ReadonlySet<string> = new Set([
  SelectedTab.FacilityModel,
  SelectedTab.ModelOperations,
  SelectedTab.CmmsExplorer,
  WORKSPACE_SETTINGS_DESTINATION.evaluations,
])

export type DestinationReuseMode =
  | 'focus-preserving-state'
  | 'focus-and-navigate'

export const destinationResourceKey = (destinationKey: string): string =>
  `destination:${destinationKey}`

export const documentResourceKey = (
  mode: 'active' | 'preview',
  documentId: string
): string => `document:${mode}:${documentId}`

export interface OpenResourceTabOptions {
  resourceKey: string
  workspaceDestination: SelectedTab
  label: string
  href: string
}

export interface OpenDocumentTabOptions {
  /**
   * Canonical, already pipeline-resolved document id. Callers must resolve the
   * active/preview configuration scope before calling (see
   * useOpenEvidenceDocument); this layer only deduplicates and navigates.
   */
  documentId: string
  pageNumber?: number
  regionHighlight?: RegionHighlight
  fragmentHighlight?: { pageNum: number; fragmentText: string }
  lotoPanel?: LotoPanelState
  label?: string
}

export const createResourceTab = (
  options: OpenResourceTabOptions
): DocumentTab => ({
  id: `resource-tab-${crypto.randomUUID()}`,
  history: [
    {
      type: 'destination',
      destinationKey: options.workspaceDestination,
      label: options.label,
      href: options.href,
    },
  ],
  currentIndex: 0,
  customLabel: options.label,
  workspaceDestination: options.workspaceDestination,
  resourceKey: options.resourceKey,
})

export const createWorkspaceDocumentTab = (
  options: OpenDocumentTabOptions,
  resourceKey: string
): DocumentTab => ({
  id: `document-resource-tab-${crypto.randomUUID()}`,
  history: [
    {
      type: 'document',
      documentId: options.documentId,
      pageNumber: options.pageNumber ?? 1,
      selectedBoxIds: [],
      query: '',
      fragmentHighlight: options.fragmentHighlight,
      regionHighlight: options.regionHighlight,
      ...(options.lotoPanel && { lotoPanel: options.lotoPanel }),
    },
  ],
  currentIndex: 0,
  customLabel: options.label,
  workspaceDestination: SelectedTab.Documents,
  resourceKey,
})
